<template>
  <div>
    <button
      @click="openModal"
      class="contact-btn"
      :class="{
        'contact-btn--blue': brand === 'alvine',
        'contact-btn--green': brand === 'munk',
      }"
    >
      <i class="fas fa-envelope"></i> {{ $t("contact.title") }}
    </button>

    <div v-if="showModal" class="contact-modal-overlay" @click="closeModal">
      <div
        class="contact-modal"
        :class="{
          'contact-modal--blue': brand === 'alvine',
          'contact-modal--green': brand === 'munk',
        }"
        @click.stop
      >
        <button class="contact-modal__close" @click="closeModal">
          <i class="fas fa-times"></i>
        </button>

        <h3 class="contact-modal__title">
          {{ $t("contact.title") }}
        </h3>

        <div v-if="feedbackMessage" class="contact-modal__feedback">
          <p>{{ feedbackMessage }}</p>
        </div>

        <div v-if="formSubmitted" class="contact-modal__success">
          <i class="fas fa-check-circle"></i>
          <p>{{ feedbackMessage }}</p>
          <button
            @click="closeModal"
            class="contact-modal__button contact-modal__button--secondary"
          >
            {{ $t("contact.close") || "Fechar" }}
          </button>
        </div>

        <form
          v-else
          class="contact-modal__form pageclip-form"
          @submit.prevent="submitForm"
        >
          <input type="hidden" name="formId" :value="formType" />

          <div class="contact-modal__form-group">
            <label for="contact-name">{{ $t("contact.name") }}</label>
            <input
              type="text"
              id="contact-name"
              name="name"
              v-model="form.name"
              :placeholder="$t('contact.namePlaceholder') || 'Seu nome'"
              required
            />
          </div>

          <div class="contact-modal__form-group">
            <label for="contact-email">{{ $t("contact.email") }}</label>
            <input
              type="email"
              id="contact-email"
              name="email"
              v-model="form.email"
              :placeholder="
                $t('contact.emailPlaceholder') || 'seuemail@exemplo.com'
              "
              required
            />
          </div>

          <div class="contact-modal__form-group">
            <label for="contact-message">{{ $t("contact.message") }}</label>
            <textarea
              id="contact-message"
              name="message"
              v-model="form.message"
              rows="4"
              :placeholder="
                $t('contact.messagePlaceholder') ||
                'Digite sua mensagem aqui...'
              "
              required
            ></textarea>
          </div>

          <button
            type="submit"
            class="contact-modal__button pageclip-form__submit"
          >
            <span>{{ $t("contact.submit") }}</span>
          </button>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "ContactForm",
  props: {
    brand: {
      type: String,
      required: true,
      default: "alvine",
    },
  },
  data() {
    return {
      showModal: false,
      formSubmitted: false,
      isSubmitting: false,
      feedbackMessage: "",
      form: {
        name: "",
        email: "",
        message: "",
      },
    };
  },
  computed: {
    currentLocale() {
      return this.$i18n.locale;
    },
    formType() {
      return this.brand === "alvine" ? "alvine-contact" : "munk-contact";
    },
  },
  methods: {
    openModal() {
      this.showModal = true;
      this.formSubmitted = false;
      this.feedbackMessage = "";

      document.addEventListener("keydown", this.handleEscape);
      document.body.style.overflow = "hidden";
    },

    closeModal() {
      this.showModal = false;
      document.removeEventListener("keydown", this.handleEscape);
      document.body.style.overflow = "";

      setTimeout(() => {
        if (!this.showModal) {
          this.resetForm();
          this.formSubmitted = false;
          this.feedbackMessage = "";
        }
      }, 300);
    },

    handleEscape(e) {
      if (e.key === "Escape" && this.showModal) {
        this.closeModal();
      }
    },

    async submitForm() {
      this.isSubmitting = true;

      try {
        const formData = new FormData();
        formData.append("name", this.form.name);
        formData.append("email", this.form.email);
        formData.append("message", this.form.message);
        formData.append("brand", this.brand);

        const siteKey = "tlFYry1pWg99fGIe4t2a9lOjcR9cG40F";
        const formType = this.formType;
        const url = `https://send.pageclip.co/${siteKey}/${formType}`;

        const response = await fetch(url, {
          method: "POST",
          body: formData,
        });

        if (response.ok) {
          console.log("Formulário enviado com sucesso");
          this.formSubmitted = true;
          this.feedbackMessage = this.$t("contact.success");
        } else {
          throw new Error("Erro ao enviar formulário: " + response.status);
        }
      } catch (error) {
        console.error("Erro ao enviar formulário:", error);
        this.feedbackMessage = this.$t("contact.error");
      } finally {
        this.isSubmitting = false;
      }
    },

    resetForm() {
      this.form = {
        name: "",
        email: "",
        message: "",
      };
    },
  },
  beforeUnmount() {
    document.removeEventListener("keydown", this.handleEscape);
    document.body.style.overflow = "";
  },
};
</script>

<style lang="scss" scoped>
@import "../assets/styles/variables";
@import "../assets/styles/mixins";

$alvine-color: $primary-color;
$munk-color: $secondary-color;

.contact-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 12px 30px;
  border-radius: 50px;
  font-family: $font-secondary;
  font-size: 1.1rem;
  font-weight: 700;
  color: white;
  border: none;
  cursor: pointer;
  transition: all $transition-normal;
  box-shadow: $shadow-md;
  min-width: 200px;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin: 0 auto;
  position: relative;
  overflow: hidden;
  z-index: 1;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 0%;
    height: 100%;
    background: rgba($white, 0.2);
    transition: width $transition-normal;
    z-index: -1;
  }

  i {
    margin-right: 10px;
  }

  &:hover {
    transform: translateY(-3px);
    box-shadow: $shadow-lg;

    &::before {
      width: 100%;
    }
  }

  &--blue {
    background: linear-gradient(135deg, $alvine-color, darken($alvine-color, 10%));
  }

  &--green {
    background: linear-gradient(135deg, $munk-color, darken($munk-color, 10%));
  }
}

.contact-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(5px);
  z-index: 1000;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  animation: fadeIn 0.3s ease-out;
}

.contact-modal {
  position: relative;
  width: 100%;
  max-width: 500px;
  background: rgba(20, 20, 20, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 40px;
  color: white;
  box-shadow: $shadow-lg;
  animation: modalSlideIn 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  max-height: 90vh;
  overflow-y: auto;

  &--blue {
    border-top: 4px solid $alvine-color;
    box-shadow: 0 20px 50px rgba($alvine-color, 0.2);
  }

  &--green {
    border-top: 4px solid $munk-color;
    box-shadow: 0 20px 50px rgba($munk-color, 0.2);
  }

  &__close {
    position: absolute;
    top: 20px;
    right: 20px;
    background: transparent;
    border: none;
    color: rgba($white, 0.5);
    font-size: 24px;
    cursor: pointer;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    transition: all $transition-normal;

    &:hover {
      background-color: rgba(255, 255, 255, 0.1);
      color: white;
      transform: rotate(90deg);
    }
  }

  &__title {
    text-align: center;
    font-family: $font-secondary;
    font-size: 2rem;
    margin-bottom: 30px;
    color: white;
    position: relative;
    padding-bottom: 15px;

    &:after {
      content: "";
      position: absolute;
      bottom: 0;
      left: 50%;
      transform: translateX(-50%);
      width: 60px;
      height: 3px;
      background-color: rgba($white, 0.1);
      border-radius: 3px;
    }

    .contact-modal--blue & {
      &:after { background-color: $alvine-color; }
    }

    .contact-modal--green & {
      &:after { background-color: $munk-color; }
    }
  }

  &__form {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  &__form-group {
    display: flex;
    flex-direction: column;
    gap: 8px;

    label {
      font-weight: 600;
      color: rgba($white, 0.8);
      font-size: 0.9rem;
      text-transform: uppercase;
      letter-spacing: 1px;
    }

    input,
    textarea {
      padding: 15px;
      background-color: rgba(255, 255, 255, 0.05);
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: 10px;
      color: white;
      font-family: $font-primary;
      font-size: 1rem;
      transition: all $transition-normal;

      &:focus {
        outline: none;
        background-color: rgba(255, 255, 255, 0.1);
        border-color: rgba(255, 255, 255, 0.3);
        box-shadow: 0 0 0 4px rgba(255, 255, 255, 0.05);
      }

      &::placeholder {
        color: rgba(255, 255, 255, 0.3);
      }
    }

    .contact-modal--blue & {
      input:focus, textarea:focus {
        border-color: $alvine-color;
        box-shadow: 0 0 0 4px rgba($alvine-color, 0.1);
      }
    }

    .contact-modal--green & {
      input:focus, textarea:focus {
        border-color: $munk-color;
        box-shadow: 0 0 0 4px rgba($munk-color, 0.1);
      }
    }
  }

  &__button {
    padding: 15px 30px;
    border-radius: 50px;
    font-family: $font-secondary;
    font-weight: 700;
    font-size: 1rem;
    text-transform: uppercase;
    letter-spacing: 1px;
    cursor: pointer;
    transition: all $transition-normal;
    border: none;
    margin-top: 10px;
    width: 100%;

    &:disabled {
      opacity: 0.7;
      cursor: not-allowed;
    }

    span {
      position: relative;
      z-index: 2;
    }
  }

  .pageclip-form__submit {
    &.contact-modal--blue, // Fallback if class added directly
    .contact-modal--blue & {
      background: linear-gradient(135deg, $alvine-color, darken($alvine-color, 10%));
      color: white;
      box-shadow: 0 10px 20px rgba($alvine-color, 0.3);

      &:hover:not(:disabled) {
        transform: translateY(-2px);
        box-shadow: 0 15px 30px rgba($alvine-color, 0.4);
      }
    }

    &.contact-modal--green, // Fallback
    .contact-modal--green & {
      background: linear-gradient(135deg, $munk-color, darken($munk-color, 10%));
      color: white;
      box-shadow: 0 10px 20px rgba($munk-color, 0.3);

      &:hover:not(:disabled) {
        transform: translateY(-2px);
        box-shadow: 0 15px 30px rgba($munk-color, 0.4);
      }
    }
  }

  &__success {
    text-align: center;
    padding: 30px 0;
    animation: fadeIn 0.5s;

    i {
      font-size: 4rem;
      margin-bottom: 20px;
      color: #4caf50;
    }

    p {
      font-size: 1.2rem;
      margin-bottom: 30px;
      color: rgba($white, 0.9);
    }
  }
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes modalSlideIn {
  from { opacity: 0; transform: translateY(30px) scale(0.95); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}

.contact-modal__feedback {
  margin-top: 20px;
  padding: 15px;
  background-color: rgba(76, 175, 80, 0.2);
  border: 1px solid #4caf50;
  color: #4caf50;
  border-radius: 8px;
  text-align: center;
  font-size: 1rem;
}

@media (max-width: 480px) {
  .contact-modal {
    padding: 25px;

    &__title {
      font-size: 1.5rem;
    }
  }
}
</style>
